import { useState, useEffect } from "react";
import { Zap, Coins, Loader2, X, Check } from "lucide-react";
import { Trans, useTranslation } from "react-i18next";
import { toast } from "sonner";
import api, { apiErr, fmtCredits } from "../lib/api";

export default function TopupModal({ open, onClose, topup }) {
  const { t } = useTranslation();
  const [packs, setPacks] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    setPacks(null);
    api.get("/payments/topup/packs")
      .then(({ data }) => setPacks(data.packs || []))
      .catch((e) => { toast.error(apiErr(e)); setPacks([]); });
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", onKey); };
  }, [open, onClose]);

  if (!open) return null;

  const buy = async (id) => {
    setBusy(id);
    try { await topup(id); } // redirige a Stripe
    catch (e) { toast.error(apiErr(e, t("topup.error"))); setBusy(null); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/70 overflow-y-auto" onClick={onClose} data-testid="topup-overlay">
      <div className="min-h-full flex items-start sm:items-center justify-center sm:p-6">
        {/* Ancho generoso (5xl): con cuatro packs, un modal estrecho los aplastaba en columnas
            donde el precio y los créditos competían por el mismo renglón. Aquí cada tarjeta
            respira y la comparación entre packs —que es LA decisión de esta pantalla— se lee
            de un vistazo. En móvil es pantalla completa a una columna. */}
        <div onClick={(e) => e.stopPropagation()} data-testid="topup-modal"
          className="relative bg-white w-full sm:max-w-5xl min-h-screen sm:min-h-0 sm:rounded-2xl shadow-2xl p-6 sm:p-10">
          <button data-testid="topup-close" onClick={onClose} className="absolute right-5 top-5 text-slate-400 hover:text-slate-700 p-1"><X size={26} /></button>
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2.5 pr-8">
            <Zap className="text-blue-600" size={30} /> {t("topup.title")}
          </h2>
          <p className="text-base sm:text-lg text-slate-600 mt-2"><Trans i18nKey="topup.subtitle" components={{ 0: <b /> }} /></p>

          {packs === null ? (
            <div className="py-24 flex justify-center"><Loader2 className="animate-spin text-blue-600" size={36} /></div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 mt-8">
              {packs.map((p, i) => {
                const best = i === packs.length - 1;
                return (
                  <div key={p.id} className={`relative rounded-2xl border-2 p-5 flex flex-col ${best ? "border-blue-600" : "border-slate-200"}`}>
                    {best && <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-[11px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap">{t("topup.bestPrice")}</span>}
                    {/* La cifra y la palabra "créditos" van SIEMPRE en el mismo renglón: partidas
                        en dos líneas se leían como dos datos distintos. De ahí el whitespace-nowrap
                        y que en móvil la rejilla baje a UNA columna: a dos, la tarjeta se
                        quedaba en ~115px útiles y la fila necesita ~130, así que no había forma
                        de mantenerlas juntas sin dejar la cifra ilegible. */}
                    <div className="flex items-center gap-1.5 text-slate-900 whitespace-nowrap">
                      <Coins size={18} className="text-blue-600 shrink-0" />
                      <span className="text-xl sm:text-2xl font-extrabold leading-none">{fmtCredits(p.seconds)}</span>
                      <span className="text-[11px] sm:text-xs font-semibold text-slate-500">{t("common.credits", { count: Math.round(p.seconds) })}</span>
                    </div>
                    <div className="mt-3 text-3xl sm:text-4xl font-extrabold text-slate-900">{p.price % 1 === 0 ? p.price : p.price.toFixed(2)}€</div>
                    {/* Cuatro decimales, no tres: con tres, los packs de 3.600, 7.200 y 18.000
                        salían los tres a "0,006" y el descuento por volumen se volvía invisible
                        —la etiqueta de "mejor precio" quedaba sin nada que la respaldara—. */}
                    <div className="text-sm text-slate-500 mt-0.5">{(p.price / p.seconds).toFixed(4).replace(".", ",")} {t("topup.perCredit")}</div>
                    <button data-testid={`topup-buy-${p.id}`} onClick={() => buy(p.id)} disabled={!!busy}
                      className="mt-5 w-full min-h-[52px] text-base rounded-xl bg-blue-600 text-white font-bold flex items-center justify-center gap-2 hover:bg-blue-700 disabled:opacity-60 transition-colors">
                      {busy === p.id ? <Loader2 className="animate-spin" size={18} /> : <Check size={18} />} {t("topup.recharge")}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          <p className="text-sm text-slate-400 text-center mt-7">{t("topup.securePay")}</p>
        </div>
      </div>
    </div>
  );
}
